import * as Switch from '@radix-ui/react-switch'
import { Dropdown } from 'components/form/inputs/Dropdown'
import { NumberInput } from 'components/form/inputs/NumberInput'
import dayjs from 'dayjs'
import { useScatterplotContext } from 'features/timeSeries'
import {
  DateInputRow,
  DateInputsContainer,
  DateLabel,
} from 'features/timeSeries/sharedStyles'
import React, { useCallback, useEffect, useState } from 'react'
import styled from 'styled-components'
import { ChartTimeUnit, DropdownOption } from 'types'

const RelativeInputContainer = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
`

const DropdownWrapper = styled.div`
  width: fit-content;
  min-width: 7rem;
`

const NumberInputWrapper = styled.div`
  width: 5rem;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`

const NegativeSymbol = styled.span`
  color: ${({ theme }) => theme.forecast.defaultText};
  font-weight: 600;
  font-size: 0.875rem;
`

const StyledSwitchRoot = styled(Switch.Root)`
  position: relative;
  width: 44px;
  height: 24px;
  background-color: ${({ theme }) => theme.forecast.defaultButton};
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  padding: 0;
  transition: background-color 150ms;

  &[data-state='checked'] {
    background-color: #50c878;
  }

  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.forecast.activeText};
    outline-offset: 2px;
  }
`

const StyledSwitchThumb = styled(Switch.Thumb)`
  display: block;
  width: 20px;
  height: 20px;
  background-color: ${({ theme }) => theme.forecast.activeText};
  border-radius: 50%;
  transition: transform 150ms;
  transform: translateX(2px);
  will-change: transform;

  &[data-state='checked'] {
    transform: translateX(22px);
  }
`

const LiveTailText = styled.span`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.forecast.defaultText};
  display: flex;
  align-items: center;
  height: 2rem;
`

const EndDateInputContainer = styled.div`
  min-height: 2rem;
  display: flex;
  align-items: center;
`

export const RelativeDateConfig: React.FC = () => {
  const {
    timeRange,
    setTimeRange,
    setLiveTail,
    relativeStart,
    setRelativeStart,
    relativeEnd,
    setRelativeEnd,
    cadenceSeconds,
  } = useScatterplotContext()

  const [localStartValue, setLocalStartValue] = useState<string>(
    relativeStart.value !== null ? String(relativeStart.value) : ''
  )
  const [localEndValue, setLocalEndValue] = useState<string>(
    relativeEnd.value !== null ? String(relativeEnd.value) : ''
  )

  // keep local values in sync if context changes externally (e.g., switching charts)
  useEffect(() => {
    setLocalStartValue(
      relativeStart.value !== null ? String(relativeStart.value) : ''
    )
  }, [relativeStart.value])

  useEffect(() => {
    setLocalEndValue(
      relativeEnd.value !== null ? String(relativeEnd.value) : ''
    )
  }, [relativeEnd.value])

  useEffect(() => {
    setLiveTail(relativeEnd.isNow)
  }, [relativeEnd.isNow, setLiveTail])

  const relativeUnitOptions: DropdownOption[] = [
    { label: 'Minutes', value: ChartTimeUnit.MINUTES },
    { label: 'Hours', value: ChartTimeUnit.HOURS },
    { label: 'Days', value: ChartTimeUnit.DAYS },
  ]

  const updateRelativeTimeRange = useCallback(
    (
      startVal?: number,
      startUnit?: ChartTimeUnit,
      isNow?: boolean,
      endVal?: number,
      endUnit?: ChartTimeUnit
    ) => {
      if (!startVal || !startUnit) return
      const now = dayjs()
      const from = now.subtract(startVal, startUnit)
      const to = isNow
        ? now
        : now.subtract(endVal || 0, endUnit || ChartTimeUnit.DAYS)
      setTimeRange({
        ...timeRange,
        from: from.toISOString(),
        to: to.toISOString(),
        preset: 'custom',
      })
    },
    [setTimeRange, timeRange]
  )

  const onStartValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    setLocalStartValue(rawValue) // allow '', partial, etc.
    const v = Number.parseInt(rawValue, 10)
    if (Number.isFinite(v) && v > 0) {
      setRelativeStart((s) => ({ ...s, value: v }))
      updateRelativeTimeRange(
        v,
        relativeStart.unit,
        relativeEnd.isNow,
        relativeEnd.value,
        relativeEnd.unit
      )
    }
  }

  const onStartValueBlur = () => {
    // if blank or invalid, snap back to context value
    const v = Number.parseInt(localStartValue, 10)
    if (!(Number.isFinite(v) && v > 0)) {
      setLocalStartValue(
        relativeStart.value !== null ? String(relativeStart.value) : ''
      )
      return
    }
    // if valid but not committed (e.g., user typed then blurred), commit
    if (relativeStart.value !== v) {
      setRelativeStart((s) => ({ ...s, value: v }))
      updateRelativeTimeRange(
        v,
        relativeStart.unit,
        relativeEnd.isNow,
        relativeEnd.value,
        relativeEnd.unit
      )
    }
  }

  const onStartUnitChange = (value: string) => {
    const u = value as ChartTimeUnit
    setRelativeStart((s) => ({ ...s, unit: u }))
    updateRelativeTimeRange(
      relativeStart.value,
      u,
      relativeEnd.isNow,
      relativeEnd.value,
      relativeEnd.unit
    )
  }

  const handleLiveTailToggle = (checked: boolean) => {
    setRelativeEnd((e) => ({ ...e, isNow: checked }))
    updateRelativeTimeRange(
      relativeStart.value,
      relativeStart.unit,
      checked,
      relativeEnd.value,
      relativeEnd.unit
    )
  }
  const onEndValueChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = e.target.value
    setLocalEndValue(rawValue)
    const v = Number.parseInt(rawValue, 10)
    if (Number.isFinite(v) && v >= 0) {
      setRelativeEnd((prev) => ({ ...prev, isNow: false, value: v }))
      updateRelativeTimeRange(
        relativeStart.value,
        relativeStart.unit,
        false,
        v,
        relativeEnd.unit
      )
    }
  }

  const onEndValueBlur = () => {
    const v = Number.parseInt(localEndValue, 10)
    if (!(Number.isFinite(v) && v >= 0)) {
      setLocalEndValue(
        relativeEnd.value !== null ? String(relativeEnd.value) : ''
      )
      return
    }
    if (relativeEnd.value !== v || relativeEnd.isNow) {
      setRelativeEnd((prev) => ({ ...prev, isNow: false, value: v }))
      const vStart = Number.parseInt(localStartValue, 10)
      updateRelativeTimeRange(
        Number.isFinite(vStart) ? vStart : relativeStart.value,
        relativeStart.unit,
        false,
        v,
        relativeEnd.unit
      )
    }
  }
  const onEndUnitChange = (value: string) => {
    const u = value as ChartTimeUnit
    setRelativeEnd((prev) => ({ ...prev, isNow: false, unit: u }))
    updateRelativeTimeRange(
      relativeStart.value,
      relativeStart.unit,
      false,
      relativeEnd.value,
      u
    )
  }

  const startValueForInput =
    localStartValue === '' ? undefined : Number.parseInt(localStartValue, 10)

  const endValueForInput =
    localEndValue === '' ? undefined : Number.parseInt(localEndValue, 10)

  return (
    <DateInputsContainer>
      <DateInputRow>
        <DateLabel>Start Date</DateLabel>
        <RelativeInputContainer>
          <NumberInputWrapper>
            <NegativeSymbol>-</NegativeSymbol>
            <NumberInput
              type='number'
              mode='edit'
              value={startValueForInput}
              onChange={onStartValueChange}
              onBlur={onStartValueBlur}
            />
          </NumberInputWrapper>
          <DropdownWrapper>
            <Dropdown
              defaultValue={relativeStart.unit}
              onValueChange={onStartUnitChange}
              options={relativeUnitOptions}
              searchable={false}
              cleanable={false}
            />
          </DropdownWrapper>
        </RelativeInputContainer>
      </DateInputRow>
      <DateInputRow>
        <DateLabel>Live Tail</DateLabel>
        <RelativeInputContainer>
          <StyledSwitchRoot
            checked={relativeEnd.isNow}
            onCheckedChange={handleLiveTailToggle}
            aria-label='Live Tail'>
            <StyledSwitchThumb />
          </StyledSwitchRoot>
        </RelativeInputContainer>
      </DateInputRow>
      <DateInputRow>
        <DateLabel>End Date</DateLabel>
        <EndDateInputContainer>
          {relativeEnd.isNow ? (
            <LiveTailText>
              Now (refreshing every {cadenceSeconds} sec)
            </LiveTailText>
          ) : (
            <RelativeInputContainer>
              <NumberInputWrapper>
                <NegativeSymbol>-</NegativeSymbol>
                <NumberInput
                  type='number'
                  mode='edit'
                  value={endValueForInput}
                  onChange={onEndValueChange}
                  onBlur={onEndValueBlur}
                />
              </NumberInputWrapper>
              <DropdownWrapper>
                <Dropdown
                  defaultValue={relativeEnd.unit}
                  onValueChange={onEndUnitChange}
                  options={relativeUnitOptions}
                  searchable={false}
                  cleanable={false}
                />
              </DropdownWrapper>
            </RelativeInputContainer>
          )}
        </EndDateInputContainer>
      </DateInputRow>
    </DateInputsContainer>
  )
}
